import { createClient } from "@supabase/supabase-js";
import type { PostgrestError, User } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

if (!supabaseUrl) {
  throw new Error("Missing VITE_SUPABASE_URL environment variable");
}

if (!supabaseAnonKey) {
  throw new Error("Missing VITE_SUPABASE_ANON_KEY environment variable");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false, // Disable auto-refresh - we'll handle manually
    persistSession: false, // Don't persist session - we'll use localStorage
    detectSessionInUrl: false, // Disable OAuth callback detection - we'll handle manually
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Custom token management
const TOKEN_KEY = "taskr_access_token";
const TOKEN_EXPIRY_KEY = "taskr_token_expiry";
const REFRESH_TOKEN_KEY = "taskr_refresh_token";
const USER_KEY = "taskr_user_data";

export interface StoredTokenData {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: User;
}

// Token management functions
export const TokenManager = {
  // Store token in localStorage and set in Supabase client
  async storeToken(tokenData: StoredTokenData): Promise<boolean> {
    try {
      localStorage.setItem(TOKEN_KEY, tokenData.access_token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, tokenData.expires_at.toString());
      localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token || "");
      localStorage.setItem(USER_KEY, JSON.stringify(tokenData.user));

      // Set the session in Supabase client immediately
      const sessionSet = await this.setSupabaseSession(
        tokenData.access_token,
        tokenData.refresh_token
      );

      if (sessionSet) {
        return true;
      } else {
        console.error("❌ Token stored but failed to set session");
        return false;
      }
    } catch (error) {
      console.error("Failed to store token:", error);
      return false;
    }
  },

  // Set Supabase session with token
  async setSupabaseSession(
    token: string,
    refreshToken?: string
  ): Promise<boolean> {
    try {
      // console.log("🔧 Setting Supabase session with token...");

      const rt = refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY) || "";

      const { error } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: rt,
      });

      if (error) {
        console.error("Failed to set Supabase session:", error.message);

        // Don't return false immediately - the token might still work for API calls
        // even if session setting fails
        return true;
      }

      // console.log("✅ Supabase session set successfully");
      return true;
    } catch (error) {
      console.error("Error setting Supabase session:", error);
      console.log(
        "⚠️ Session setting failed with exception, but continuing anyway"
      );
      // Don't return false - the token might still work for API calls
      return true;
    }
  },

  // Ensure token is available and session is set
  async ensureTokenReady(): Promise<boolean> {
    try {
      const token = await this.getStoredTokenAsync();
      if (!token) {
        return false;
      }

      const isExpired = await this.isTokenExpiredAsync();
      if (isExpired) {
        return false;
      }

      // Skip session setting since it's failing and not needed for direct API calls
      // The token will be used directly in the Authorization header

      return true;
    } catch (error) {
      console.error("Error ensuring token ready:", error);
      return false;
    }
  },

  // Get token from localStorage (async version)
  async getStoredTokenAsync(): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        resolve(token);
      } catch (error) {
        console.error("Failed to get stored token:", error);
        resolve(null);
      }
    });
  },

  // Get token from localStorage (sync version for compatibility)
  getStoredToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get stored token:", error);
      return null;
    }
  },

  // Alias for compatibility
  getToken(): string | null {
    return this.getStoredToken();
  },

  // Get stored user data (async version)
  async getStoredUserAsync(): Promise<User | null> {
    return new Promise((resolve) => {
      try {
        const userData = localStorage.getItem(USER_KEY);
        resolve(userData ? JSON.parse(userData) : null);
      } catch (error) {
        console.error("Failed to get stored user:", error);
        resolve(null);
      }
    });
  },

  // Get stored user data (sync version for compatibility)
  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem(USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Failed to get stored user:", error);
      return null;
    }
  },

  // Check if token is expired (async version)
  async isTokenExpiredAsync(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (!expiryStr) {
          resolve(true);
          return;
        }

        const expiry = parseInt(expiryStr);
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = expiry - now;

        // Consider token expired if less than 5 minutes remaining
        resolve(timeUntilExpiry <= 300);
      } catch (error) {
        console.error("Failed to check token expiry:", error);
        resolve(true);
      }
    });
  },

  // Check if token is expired (sync version for compatibility)
  isTokenExpired(): boolean {
    try {
      const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
      if (!expiryStr) return true;

      const expiry = parseInt(expiryStr);
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiry - now;

      // Consider token expired if less than 5 minutes remaining
      return timeUntilExpiry <= 300;
    } catch (error) {
      console.error("Failed to check token expiry:", error);
      return true;
    }
  },

  // Clear stored token (async version)
  async clearTokenAsync(): Promise<void> {
    return new Promise((resolve) => {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);

        resolve();
      } catch (error) {
        console.error("Failed to clear token:", error);
        resolve();
      }
    });
  },

  // Clear stored token (sync version for compatibility)
  clearToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (error) {
      console.error("Failed to clear token:", error);
    }
  },

  // Get authorization header for API calls (async version)
  async getAuthHeaderAsync(): Promise<Record<string, string>> {
    const token = await this.getStoredTokenAsync();
    if (!token) {
      throw new Error("No authentication token available");
    }

    if (await this.isTokenExpiredAsync()) {
      throw new Error("Authentication token has expired");
    }

    return {
      Authorization: `Bearer ${token}`,
      apikey: supabaseAnonKey,
    };
  },

  // Get authorization header for API calls (sync version for compatibility)
  getAuthHeader(): Record<string, string> {
    const token = this.getStoredToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    if (this.isTokenExpired()) {
      throw new Error("Authentication token has expired");
    }

    return {
      Authorization: `Bearer ${token}`,
      apikey: supabaseAnonKey,
    };
  },

  // Validate token with Supabase
  async validateTokenWithSupabase(): Promise<boolean> {
    try {
      const token = await this.getStoredTokenAsync();
      if (!token) return false;

      // Method 1: Use Supabase auth.getUser() to validate token
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser(token);

      if (error || !user) {
        console.log("🚨 Token validation failed:", error?.message);
        return false;
      }

      console.log("✅ Token validated successfully with Supabase");
      return true;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  },

  // Alternative validation method using API call
  async validateTokenWithAPI(): Promise<boolean> {
    try {
      const token = await this.getStoredTokenAsync();
      if (!token) return false;

      // Make a simple API call to test token
      const response = await fetch(
        `${supabaseUrl}/rest/v1/user_profiles?limit=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: supabaseAnonKey,
          },
        }
      );

      const isValid = response.status !== 401;
      console.log(
        isValid
          ? "✅ Token validated via API call"
          : "🚨 Token invalid via API call"
      );
      return isValid;
    } catch (error) {
      console.error("API token validation error:", error);
      return false;
    }
  },

  // Comprehensive token validation
  async validateToken(): Promise<boolean> {
    // First check local expiry
    if (await this.isTokenExpiredAsync()) {
      console.log("🚨 Token expired locally");
      return false;
    }

    // Then validate with Supabase
    const isValid = await this.validateTokenWithSupabase();

    if (!isValid) {
      await this.clearTokenAsync();
      window.dispatchEvent(new CustomEvent("tokenExpired"));
    }

    return isValid;
  },

  // Get user info from JWT token without making API calls
  getUserFromToken(token: string): User | null {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return {
        id: payload.sub,
        email: payload.email,
        email_confirmed_at: payload.email_confirmed_at,
        created_at: payload.created_at,
        user_metadata: payload.user_metadata || {},
        app_metadata: payload.app_metadata || {},
        aud: payload.aud,
        role: payload.role,
      } as User;
    } catch (error) {
      console.error("Failed to decode user from token:", error);
      return null;
    }
  },
};

// Helper function to handle API calls with automatic token validation
export const withAuthCheck = async <T>(
  apiCall: () => Promise<{ data: T; error: PostgrestError | null }>
): Promise<{ data: T; error: PostgrestError | null }> => {
  try {
    // Check token before making the call
    if (await TokenManager.isTokenExpiredAsync()) {
      console.log("🚨 Token expired, redirecting to login...");
      await TokenManager.clearTokenAsync();

      // Dispatch event to trigger app-wide logout
      window.dispatchEvent(new CustomEvent("tokenExpired"));

      return {
        data: null as T,
        error: {
          message: "Session expired",
          details: "",
          hint: "",
          code: "SESSION_EXPIRED",
          name: "SessionExpiredError",
        } as PostgrestError,
      };
    }

    // Ensure token is ready before API call
    const tokenReady = await TokenManager.ensureTokenReady();
    if (!tokenReady) {
      console.log("🚨 Token not ready, cannot make API call");
      await TokenManager.clearTokenAsync();
      window.dispatchEvent(new CustomEvent("tokenExpired"));

      return {
        data: null as T,
        error: {
          message: "Authentication token not available",
          details: "",
          hint: "",
          code: "TOKEN_NOT_READY",
          name: "TokenNotReadyError",
        } as PostgrestError,
      };
    }

    const result = await apiCall();

    // Check if the error indicates an auth issue
    if (
      result.error &&
      (result.error.message?.includes("JWT") ||
        result.error.message?.includes("token") ||
        result.error.message?.includes("expired") ||
        result.error.code === "PGRST301") // PostgreSQL auth error
    ) {
      console.log("🚨 API call failed due to auth error, clearing token...");
      await TokenManager.clearTokenAsync();

      // Dispatch event to trigger app-wide logout
      window.dispatchEvent(new CustomEvent("tokenExpired"));

      return {
        data: null as T,
        error: {
          message: "Session expired",
          details: "",
          hint: "",
          code: "SESSION_EXPIRED",
          name: "SessionExpiredError",
        } as PostgrestError,
      };
    }

    return result;
  } catch (error) {
    console.error("API call error:", error);
    return {
      data: null as T,
      error:
        error instanceof Error
          ? ({
              message: error.message,
              details: "",
              hint: "",
              code: "UNKNOWN_ERROR",
              name: "UnknownError",
            } as PostgrestError)
          : null,
    };
  }
};

// Helper function to make authenticated fetch requests
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  try {
    const authHeaders = await TokenManager.getAuthHeaderAsync();

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options.headers,
      },
    });

    // If we get a 401, the token might be expired
    if (response.status === 401) {
      console.log("🚨 API returned 401, token likely expired");
      await TokenManager.clearTokenAsync();
      window.dispatchEvent(new CustomEvent("tokenExpired"));
      throw new Error("Authentication failed - token expired");
    }

    return response;
  } catch (error) {
    if (error instanceof Error && error.message.includes("expired")) {
      await TokenManager.clearTokenAsync();
      window.dispatchEvent(new CustomEvent("tokenExpired"));
    }
    throw error;
  }
};
